import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import trackEvent from 'lib/posthog/trackEvent';

const AUTOPLAY_MS = 6000;
// Horizontal gap between banners (matches the `gap-x-4` on the track).
// Scroll offsets must account for it so autoplay/dots stay in sync.
const GAP_PX = 16;
// Window during which the scroll→activeIndex sync is suppressed after a
// programmatic scroll, so intermediate positions don't flicker the dots.
const SCROLL_SETTLE_MS = 500;

const HomeBannerCarouselItem = ({
  ad,
  gaEventCategory,
  width,
  height,
  mobileWidth,
  mobileHeight,
  tabletWidth,
  tabletHeight,
  priority,
  index,
}) => {
  const bannerRef = useRef(null);
  const viewFiredRef = useRef(false);

  useIntersectionObserver({
    target: bannerRef,
    threshold: 0.5,
    // Fires once per banner per mount — ref guards against duplicate view events
    // when the user scrolls back to a previously seen banner.
    onIntersect: () => {
      if (viewFiredRef.current) return;
      viewFiredRef.current = true;
      trackEvent(
        'view home banner',
        {
          type: 'banner',
          source_type: 'scroll',
          source_value: ad.description,
          event_category: 'home',
          event_label: ad.description,
          position: index,
        },
        { destination: 'ga4' }
      );
    },
  });

  return (
    <a
      ref={bannerRef}
      className="w-full flex-shrink-0 snap-start"
      href={ad.url}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => {
        sendEvent({
          action: `small_ad_click__${ad.id}`,
          category: gaEventCategory,
          label: ad.visibility,
        });
        trackEvent(
          'click home banner',
          {
            source_type: 'interaction',
            source_value: ad.description,
            event_category: 'home',
            event_label: ad.description,
            position: index,
          },
          { destination: 'ga4' }
        );
      }}
    >
      <picture className="w-full">
        {ad.mobileImage && (
          <source
            // Only carry slot dimensions when the source is a distinct image —
            // processedLandscapeAds falls back to ad.image when no mobile
            // variant is uploaded, in which case the desktop dims apply.
            height={ad.mobileImage !== ad.image ? mobileHeight : height}
            media="(max-width: 767px)"
            srcSet={ad.mobileImage}
            width={ad.mobileImage !== ad.image ? mobileWidth : width}
          />
        )}
        {ad.tabletImage && (
          <source
            height={ad.tabletImage !== ad.image ? tabletHeight : height}
            media="(max-width: 1023px)"
            srcSet={ad.tabletImage}
            width={ad.tabletImage !== ad.image ? tabletWidth : width}
          />
        )}
        <img
          alt={ad.description}
          className="w-full rounded-3"
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          src={ad.image}
          width={width}
        />
      </picture>
    </a>
  );
};

HomeBannerCarouselItem.propTypes = {
  ad: PropTypes.shape({
    description: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    image: PropTypes.string.isRequired,
    mobileImage: PropTypes.string,
    tabletImage: PropTypes.string,
    url: PropTypes.string.isRequired,
    visibility: PropTypes.string,
  }).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  mobileHeight: PropTypes.number,
  mobileWidth: PropTypes.number,
  priority: PropTypes.bool,
  tabletHeight: PropTypes.number,
  tabletWidth: PropTypes.number,
  width: PropTypes.number.isRequired,
};

const HomeBannerCarousel = ({
  ads = [],
  gaEventCategory,
  width,
  height,
  mobileWidth,
  mobileHeight,
  tabletWidth,
  tabletHeight,
}) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);
  // True while a programmatic smooth scroll is in flight — see scrollToIndex.
  const navigatingRef = useRef(false);
  const navTimeoutRef = useRef(null);

  const adsSorted = useMemo(
    () => [...ads].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [ads]
  );

  const scrollToIndex = useCallback((index) => {
    const el = containerRef.current;
    if (!el) return;
    // Mark the programmatic scroll so onScroll ignores the intermediate
    // positions the smooth animation passes through (dot-flicker fix).
    navigatingRef.current = true;
    if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
    navTimeoutRef.current = setTimeout(() => {
      navigatingRef.current = false;
    }, SCROLL_SETTLE_MS);
    el.scrollTo({
      left: index * (el.clientWidth + GAP_PX),
      behavior: 'smooth',
    });
  }, []);

  const resetAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % adsSorted.length;
        scrollToIndex(next);
        return next;
      });
    }, AUTOPLAY_MS);
  }, [adsSorted.length, scrollToIndex]);

  const goTo = useCallback(
    (index) => {
      scrollToIndex(index);
      setActiveIndex(index);
      resetAutoplay();
    },
    [scrollToIndex, resetAutoplay]
  );

  const goPrev = useCallback(() => {
    goTo((activeIndex - 1 + adsSorted.length) % adsSorted.length);
  }, [activeIndex, adsSorted.length, goTo]);

  const goNext = useCallback(() => {
    goTo((activeIndex + 1) % adsSorted.length);
  }, [activeIndex, adsSorted.length, goTo]);

  useEffect(() => {
    if (adsSorted.length <= 1) return;
    resetAutoplay();
    return () => {
      clearInterval(timerRef.current);
      clearTimeout(navTimeoutRef.current);
    };
  }, [resetAutoplay, adsSorted.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Derives activeIndex from scroll position so the dots stay in sync when
    // the user swipes manually — programmatic navigation (goTo/autoplay) sets
    // activeIndex directly.
    const onScroll = () => {
      // Ignore scroll events fired by a programmatic goTo/autoplay animation.
      if (navigatingRef.current) return;
      const index = Math.round(el.scrollLeft / (el.clientWidth + GAP_PX));
      setActiveIndex(Math.min(index, adsSorted.length - 1));
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [adsSorted.length]);

  if (adsSorted.length === 0) return null;

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="flex w-full snap-x snap-mandatory gap-x-4 overflow-x-scroll scroll-smooth scrollbar-none"
        data-testid="banner-track"
      >
        {adsSorted.map((ad, index) => (
          <HomeBannerCarouselItem
            key={ad.id}
            ad={ad}
            gaEventCategory={gaEventCategory}
            height={height}
            index={index}
            mobileHeight={mobileHeight}
            mobileWidth={mobileWidth}
            priority={index === 0}
            tabletHeight={tabletHeight}
            tabletWidth={tabletWidth}
            width={width}
          />
        ))}
      </div>

      {adsSorted.length > 1 && (
        <>
          <button
            aria-label="Banner anterior"
            className="border-white/20 bg-white/15 shadow-lg hover:bg-white/25 dark:border-white/10 dark:bg-white/10 dark:hover:bg-white/20 absolute left-2 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border p-1.5 backdrop-blur-md transition-all lg:flex"
            type="button"
            onClick={goPrev}
          >
            <svg
              fill="none"
              height="20"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            aria-label="Próximo banner"
            className="border-white/20 bg-white/15 shadow-lg hover:bg-white/25 dark:border-white/10 dark:bg-white/10 dark:hover:bg-white/20 absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border p-1.5 backdrop-blur-md transition-all lg:flex"
            type="button"
            onClick={goNext}
          >
            <svg
              fill="none"
              height="20"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
          <div className="mt-2 flex w-full items-center justify-center gap-x-2">
            {adsSorted.map((_, i) => (
              <button
                key={i}
                aria-label={`Ir para banner ${i + 1}`}
                className={twJoin(
                  'size-2 rounded-full transition-colors',
                  i === activeIndex
                    ? 'bg-neutral-low-400 dark:bg-neutral-high-200'
                    : 'bg-neutral-high-300 dark:bg-neutral-low-300'
                )}
                type="button"
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

HomeBannerCarousel.propTypes = {
  ads: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      mobileImage: PropTypes.string,
      tabletImage: PropTypes.string,
      url: PropTypes.string.isRequired,
      visibility: PropTypes.string,
      position: PropTypes.number,
    })
  ),
  gaEventCategory: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  mobileHeight: PropTypes.number,
  mobileWidth: PropTypes.number,
  tabletHeight: PropTypes.number,
  tabletWidth: PropTypes.number,
  width: PropTypes.number.isRequired,
};

export default HomeBannerCarousel;
